import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Dropdown, SearchBox, Checkbox, Tag, Button } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';

export interface CascadeOption {
  text: string;
  value: string;
  children?: CascadeOption[];
}

export type ReturnMode = 'minimal' | 'smart' | 'full';

export interface LevelValue {
  level: number;
  values: string[];
  isAllSelected: boolean;
}

export interface CascadeSelectFilterProps {
  options: CascadeOption[];
  /** 受控模式：外部 value 变化会同步到内部渲染 */
  value?: string[];
  /** 非受控模式：仅用于初始化 */
  defaultValue?: string[];
  onChange?: (value: string[] | LevelValue[], rawLeafValues?: string[]) => void;
  className?: string;
  placeholder?: string;
  /** 下拉关闭时触发，返回转换后的值和原始叶子值 */
  onClose?: (value: string[] | LevelValue[], rawLeafValues?: string[]) => void;
  levelTitles?: string[];
  emptyAsAll?: boolean;
  returnMode?: ReturnMode;
}

interface NodeSelectionState {
  checked: boolean;
  indeterminate: boolean;
}

export default function CascadeSelectFilter(props: CascadeSelectFilterProps) {
  const {
    options,
    value: controlledValue,
    defaultValue,
    onChange,
    className,
    placeholder,
    onClose,
    levelTitles,
    emptyAsAll,
    returnMode,
  } = props;
  const { formatMessage } = useLanguage();

  const isControlled = controlledValue !== undefined;
  const [searchText, setSearchText] = useState('');
  const [selectedPath, setSelectedPath] = useState<string[]>([]);

  // Flatten all leaf nodes for value mapping
  const leafNodesMap = useMemo(() => {
    const map = new Map<string, CascadeOption>();
    const traverse = (nodes: CascadeOption[]) => {
      nodes.forEach((node) => {
        if (!node.children || node.children.length === 0) {
          map.set(node.value, node);
        } else {
          traverse(node.children);
        }
      });
    };
    traverse(options);
    return map;
  }, [options]);

  const allLeafValues = useMemo(() => Array.from(leafNodesMap.keys()), [leafNodesMap]);

  // 用 ref 持有最新转换函数，避免 useEffect 因引用变化反复触发
  const toEffectiveRef = useRef((v: string[]) => v);
  toEffectiveRef.current = useCallback(
    (v: string[]) => (emptyAsAll && v.length === 0 ? allLeafValues : v),
    [emptyAsAll, allLeafValues],
  );

  // 内部状态：管理选中的叶子节点
  const [internalLeafValues, setInternalLeafValues] = useState<string[]>(() => {
    const v = isControlled ? controlledValue : (defaultValue ?? []);
    return emptyAsAll && v.length === 0 ? allLeafValues : v;
  });

  // 受控模式：外部 value 变化同步到内部
  useEffect(() => {
    if (isControlled) {
      setInternalLeafValues(toEffectiveRef.current(controlledValue!));
    }
  }, [isControlled, controlledValue, emptyAsAll, allLeafValues]);

  // Initialize selectedPath with first item from each level to ensure all levels are expanded
  useEffect(() => {
    if (options.length > 0) {
      const initialPath: string[] = [];
      let currentNodes = options;

      while (currentNodes.length > 0) {
        const firstNode = currentNodes[0];
        initialPath.push(firstNode.value);

        if (firstNode.children && firstNode.children.length > 0) {
          currentNodes = firstNode.children;
        } else {
          break;
        }
      }

      setSelectedPath(initialPath);
    }
  }, [options]);

  // Get all leaf nodes from selected values
  const selectedLeafNodes = useMemo(() => {
    return internalLeafValues
      .map((v) => leafNodesMap.get(v))
      .filter(Boolean) as CascadeOption[];
  }, [internalLeafValues, leafNodesMap]);

  // Get dynamic levels based on selected path for cascading navigation
  const getDynamicLevels = useCallback(
    (nodes: CascadeOption[], path: string[]): CascadeOption[][] => {
      const levels: CascadeOption[][] = [nodes];

      let currentNodes = nodes;
      for (const selectedValue of path) {
        const selectedNode = currentNodes.find(
          (n) => n.value === selectedValue,
        );

        if (!selectedNode?.children?.length) break;

        levels.push(selectedNode.children);
        currentNodes = selectedNode.children;
      }

      return levels;
    },
    [],
  );

  const levels = useMemo(
    () => getDynamicLevels(options, selectedPath),
    [options, selectedPath, getDynamicLevels],
  );

  // Handle clicking on text to navigate to children and auto-expand all subsequent levels
  const handleTextClick = useCallback(
    (node: CascadeOption, levelIndex: number) => {
      if (!node.children || node.children.length === 0) return;

      const newPath = selectedPath.slice(0, levelIndex);
      newPath.push(node.value);

      let currentNode = node;
      while (currentNode.children && currentNode.children.length > 0) {
        const firstChild = currentNode.children[0];
        newPath.push(firstChild.value);
        currentNode = firstChild;
      }

      setSelectedPath(newPath);
    },
    [selectedPath],
  );

  // Calculate selection state for each node
  const getNodeSelectionState = useCallback(
    (node: CascadeOption, selectedValues: Set<string>): NodeSelectionState => {
      if (!node.children || node.children.length === 0) {
        return {
          checked: selectedValues.has(node.value),
          indeterminate: false,
        };
      }

      const childStates = node.children.map((child) =>
        getNodeSelectionState(child, selectedValues),
      );
      const allChecked = childStates.every((state) => state.checked);
      const someChecked = childStates.some(
        (state) => state.checked || state.indeterminate,
      );

      return {
        checked: allChecked,
        indeterminate: !allChecked && someChecked,
      };
    },
    [],
  );

  // Get all descendant leaf values of a node
  const getDescendantLeafValues = useCallback(
    (node: CascadeOption): string[] => {
      if (!node.children || node.children.length === 0) {
        return [node.value];
      }
      const leaves: string[] = [];
      node.children.forEach((child) => {
        leaves.push(...getDescendantLeafValues(child));
      });
      return leaves;
    },
    [],
  );

  // Smart compress: all-selected subtree → parent value only, partial → recurse
  const smartCompress = useCallback(
    (
      nodes: CascadeOption[],
      selectedSet: Set<string>,
      currentLevel = 0,
    ): LevelValue[] => {
      const levelValues: string[] = [];
      const childResults: LevelValue[] = [];

      for (const node of nodes) {
        if (!node.children || node.children.length === 0) {
          if (selectedSet.has(node.value)) {
            levelValues.push(node.value);
          }
        } else {
          const leaves = getDescendantLeafValues(node);
          const allSelected = leaves.every((l) => selectedSet.has(l));
          const someSelected = leaves.some((l) => selectedSet.has(l));

          if (allSelected) {
            levelValues.push(node.value);
          } else if (someSelected) {
            levelValues.push(node.value);
            childResults.push(
              ...smartCompress(node.children, selectedSet, currentLevel + 1),
            );
          }
        }
      }

      const result: LevelValue[] = [];
      if (levelValues.length > 0) {
        result.push({
          level: currentLevel,
          values: levelValues,
          isAllSelected: false,
        });
      }
      result.push(...childResults);
      return result;
    },
    [getDescendantLeafValues],
  );

  // Full levels: collect all levels with isAllSelected flag
  const buildFullLevels = useCallback(
    (
      nodes: CascadeOption[],
      selectedSet: Set<string>,
      currentLevel = 0,
    ): LevelValue[] => {
      const levelValues: string[] = [];
      const childResults: LevelValue[] = [];

      for (const node of nodes) {
        const leaves = getDescendantLeafValues(node);
        const hasSelected = leaves.some((l) => selectedSet.has(l));
        if (hasSelected) {
          levelValues.push(node.value);
        }
        if (node.children && node.children.length > 0) {
          childResults.push(
            ...buildFullLevels(node.children, selectedSet, currentLevel + 1),
          );
        }
      }

      const result: LevelValue[] = [];
      if (levelValues.length > 0) {
        const allLeaves = nodes.flatMap((n) => getDescendantLeafValues(n));
        const allSelected = allLeaves.every((l) => selectedSet.has(l));
        result.push({
          level: currentLevel,
          values: levelValues,
          isAllSelected: allSelected,
        });
      }
      result.push(...childResults);
      return result;
    },
    [getDescendantLeafValues],
  );

  // Transform return value based on returnMode
  const transformReturnValue = useCallback(
    (leafValues: string[]): string[] | LevelValue[] => {
      if (!returnMode || returnMode === 'minimal') return leafValues;
      const selectedSet = new Set(leafValues);

      if (returnMode === 'smart') {
        if (emptyAsAll && leafValues.length === 0) return [];
        const allLeaves = Array.from(leafNodesMap.keys());
        if (emptyAsAll && allLeaves.every((l) => selectedSet.has(l))) return [];
        return smartCompress(options, selectedSet);
      }

      if (returnMode === 'full') {
        return buildFullLevels(options, selectedSet);
      }

      return leafValues;
    },
    [
      returnMode,
      options,
      emptyAsAll,
      leafNodesMap,
      smartCompress,
      buildFullLevels,
    ],
  );

  // Toggle selection for a node
  const toggleNodeSelection = useCallback(
    (node: CascadeOption, selectedValues: Set<string>) => {
      const state = getNodeSelectionState(node, selectedValues);
      const newValues = new Set(selectedValues);

      if (state.checked) {
        const leaves = getDescendantLeafValues(node);
        leaves.forEach((leaf) => newValues.delete(leaf));
      } else {
        const leaves = getDescendantLeafValues(node);
        leaves.forEach((leaf) => newValues.add(leaf));
      }

      return newValues;
    },
    [getNodeSelectionState, getDescendantLeafValues],
  );

  // Handle checkbox change
  const handleCheckboxChange = useCallback(
    (node: CascadeOption, checked: boolean, levelIndex: number) => {
      const selectedSet = new Set(internalLeafValues);
      const newValues = toggleNodeSelection(node, selectedSet);
      const leafValues = Array.from(newValues);
      setInternalLeafValues(leafValues);
      onChange?.(transformReturnValue(leafValues), leafValues);

      if (node.children && node.children.length > 0) {
        const newPath = selectedPath.slice(0, levelIndex);
        newPath.push(node.value);
        let currentNode = node;
        while (currentNode.children && currentNode.children.length > 0) {
          const firstChild = currentNode.children[0];
          newPath.push(firstChild.value);
          currentNode = firstChild;
        }
        setSelectedPath(newPath);
      }
    },
    [
      internalLeafValues,
      onChange,
      toggleNodeSelection,
      transformReturnValue,
      selectedPath,
    ],
  );

  // Handle Select All for a level
  const handleSelectAllLevel = useCallback(
    (levelIndex: number) => {
      const levelNodes = levels[levelIndex];
      if (!levelNodes) return;

      const selectedSet = new Set(internalLeafValues);
      levelNodes.forEach((node) => {
        const leaves = getDescendantLeafValues(node);
        leaves.forEach((leaf) => selectedSet.add(leaf));
      });
      const leafValues = Array.from(selectedSet);
      setInternalLeafValues(leafValues);
      onChange?.(transformReturnValue(leafValues), leafValues);
    },
    [
      levels,
      internalLeafValues,
      onChange,
      getDescendantLeafValues,
      transformReturnValue,
    ],
  );

  // Handle Clear All for a level
  const handleClearAllLevel = useCallback(
    (levelIndex: number) => {
      const levelNodes = levels[levelIndex];
      if (!levelNodes) return;

      const selectedSet = new Set(internalLeafValues);
      levelNodes.forEach((node) => {
        const leaves = getDescendantLeafValues(node);
        leaves.forEach((leaf) => selectedSet.delete(leaf));
      });
      const leafValues = Array.from(selectedSet);
      setInternalLeafValues(leafValues);
      onChange?.(transformReturnValue(leafValues), leafValues);
    },
    [
      levels,
      internalLeafValues,
      onChange,
      getDescendantLeafValues,
      transformReturnValue,
    ],
  );

  // Check if a level has any selected items
  const hasLevelSelection = useCallback(
    (levelIndex: number): boolean => {
      const levelNodes = levels[levelIndex];
      if (!levelNodes) return false;

      const selectedSet = new Set(internalLeafValues);
      for (const node of levelNodes) {
        const leaves = getDescendantLeafValues(node);
        if (leaves.some((leaf) => selectedSet.has(leaf))) {
          return true;
        }
      }
      return false;
    },
    [levels, internalLeafValues, getDescendantLeafValues],
  );

  // Handle tag removal
  const handleTagRemove = useCallback(
    (tagValue: string) => {
      const newValue = internalLeafValues.filter((v) => v !== tagValue);
      setInternalLeafValues(newValue);
      onChange?.(transformReturnValue(newValue), newValue);
    },
    [internalLeafValues, onChange, transformReturnValue],
  );

  // Handle search
  const handleSearchChange = useCallback((val: string) => {
    setSearchText(val);
  }, []);

  // Filter nodes by search text
  const filterNodes = useCallback(
    (nodes: CascadeOption[]): CascadeOption[] => {
      if (!searchText) return nodes;

      const filtered: CascadeOption[] = [];
      nodes.forEach((node) => {
        const matchesSearch = node.text
          .toLowerCase()
          .includes(searchText.toLowerCase());
        const filteredChildren = node.children
          ? filterNodes(node.children)
          : [];

        if (matchesSearch || filteredChildren.length > 0) {
          filtered.push({
            ...node,
            children:
              filteredChildren.length > 0 ? filteredChildren : node.children,
          });
        }
      });

      return filtered;
    },
    [searchText],
  );

  const filteredOptions = useMemo(
    () => filterNodes(options),
    [options, filterNodes],
  );
  const filteredLevels = useMemo(
    () => getDynamicLevels(filteredOptions, selectedPath),
    [filteredOptions, selectedPath, getDynamicLevels],
  );

  // Get total count of leaf nodes
  const totalLeafCount = useMemo(() => leafNodesMap.size, [leafNodesMap]);

  // Calculate display text for button
  const displayText = useMemo(() => {
    const selectedCount = internalLeafValues.length || 0;

    if (selectedCount === 0) {
      return emptyAsAll
        ? formatMessage({ id: 'All' })
        : formatMessage({ id: 'None' });
    }
    if (selectedCount === totalLeafCount) {
      return formatMessage({ id: 'All' });
    }
    if (selectedCount <= 2) {
      const selectedTexts = selectedLeafNodes.map((node) => node.text);
      return selectedTexts.join(', ');
    }
    return `${selectedCount}`;
  }, [
    internalLeafValues.length,
    totalLeafCount,
    selectedLeafNodes,
    formatMessage,
    emptyAsAll,
  ]);

  return (
    <Dropdown
      className={`dbo-cascade-select-filter ${className ? className : ''}`}
      appearance="button"
      matchButtonWidth={false}
      clickClose={false}
      onClose={() => {
        onClose?.(transformReturnValue(internalLeafValues), internalLeafValues);
      }}
      button={
        <div className="dbo-select-button">
          <span className="dbo-select-button-text">
            {placeholder && (
              <span className="dbo-select-button-placeholder">
                {placeholder}{' '}
              </span>
            )}
            <span className="dbo-select-button-value">{displayText}</span>
          </span>
        </div>
      }
    >
      {(close) => (
        <div className="dbo-cascade-dropdown-content">
          {/* Search Box */}
          <div className="dbo-cascade-search">
            <div
              className={`dbo-cascade-search-wrapper${
                selectedLeafNodes.length > 0 ? ' has-tags' : ''
              }`}
            >
              {selectedLeafNodes.map((node) => (
                <Tag
                  key={node.value}
                  className="dbo-cascade-tag-item"
                  onClose={() => handleTagRemove(node.value)}
                >
                  {node.text}
                </Tag>
              ))}
              <SearchBox
                placeholder={
                  selectedLeafNodes.length > 0
                    ? ''
                    : formatMessage({ id: 'Search' })
                }
                value={searchText}
                onChange={handleSearchChange}
                onClear={() => setSearchText('')}
              />
            </div>
          </div>

          {/* Level Panels */}
          <div className="dbo-cascade-levels">
            {filteredLevels.map((level, levelIndex) => (
              <div key={levelIndex} className="dbo-cascade-level">
                {/* Level Header */}
                <div className="dbo-cascade-level-header">
                  <span className="dbo-cascade-level-title">
                    {levelTitles?.[levelIndex] ??
                      formatMessage({ id: `Level ${levelIndex + 1}` })}
                  </span>
                  <div className="dbo-cascade-level-actions">
                    {hasLevelSelection(levelIndex) ? (
                      <>
                        <Button
                          type="link"
                          onClick={() => handleClearAllLevel(levelIndex)}
                        >
                          {formatMessage({ id: 'Clear All' })}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="link"
                          onClick={() => handleSelectAllLevel(levelIndex)}
                        >
                          {formatMessage({ id: 'Select All' })}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Level List */}
                <div className="dbo-cascade-level-list">
                  {level.map((node) => {
                    const state = getNodeSelectionState(
                      node,
                      new Set(internalLeafValues),
                    );
                    const hasChildren =
                      node.children && node.children.length > 0;
                    const isSelectedInPath =
                      selectedPath[levelIndex] === node.value;
                    const isLastLevel = !hasChildren;

                    return (
                      <div key={node.value} className="dbo-cascade-item">
                        <div
                          className={`dbo-cascade-item-content ${
                            !isLastLevel && isSelectedInPath ? 'selected' : ''
                          }`}
                        >
                          <Checkbox
                            value={state.checked}
                            indeterminate={state.indeterminate}
                            onChange={(checked) =>
                              handleCheckboxChange(node, checked as boolean, levelIndex)
                            }
                          >
                          </Checkbox>
                          <span
                            className={`dbo-cascade-item-text ${
                              hasChildren ? 'clickable' : ''
                            }`}
                            onClick={() =>
                              isLastLevel
                                ? handleCheckboxChange(node, !state.checked, levelIndex)
                                : handleTextClick(node, levelIndex)
                            }
                          >
                            {node.text}
                          </span>
                          {hasChildren && (
                            <span className="dbo-cascade-arrow">▶</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Dropdown>
  );
}
